import * as THREE from 'three';

/**
 * Plan-view orientation aids (spec §2.6): an optional ground grid and a north indicator. These are
 * overlay helpers (attached to the scene overlay root), not domain data.
 */

export interface GridOptions {
  widthMeters: number;
  lengthMeters: number;
  /** Grid spacing in meters. */
  spacing?: number;
  color?: THREE.ColorRepresentation;
}

/** Build a grid of lines spanning the terrain footprint on the y=0 plane. */
export function createGrid(options: GridOptions): THREE.LineSegments {
  const spacing = options.spacing ?? 5;
  const halfW = options.widthMeters / 2;
  const halfL = options.lengthMeters / 2;
  const points: number[] = [];

  for (let x = -halfW; x <= halfW + 1e-6; x += spacing) {
    points.push(x, 0.01, -halfL, x, 0.01, halfL);
  }
  for (let z = -halfL; z <= halfL + 1e-6; z += spacing) {
    points.push(-halfW, 0.01, z, halfW, 0.01, z);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
  const material = new THREE.LineBasicMaterial({
    color: options.color ?? 0x2f3a2a,
    transparent: true,
    opacity: 0.25,
  });
  const grid = new THREE.LineSegments(geometry, material);
  grid.name = 'terrain-grid';
  return grid;
}

/**
 * Build a simple north indicator arrow pointing along -Z (north) in terrain-local space. Rotate the
 * returned group by the site's north orientation to keep it aligned.
 */
export function createNorthIndicator(sizeMeters = 4): THREE.Group {
  const group = new THREE.Group();
  group.name = 'north-indicator';

  const shaftGeom = new THREE.CylinderGeometry(0.05, 0.05, sizeMeters, 8);
  shaftGeom.translate(0, 0, 0);
  shaftGeom.rotateX(Math.PI / 2);
  const material = new THREE.MeshBasicMaterial({ color: 0xd94c3d });
  const shaft = new THREE.Mesh(shaftGeom, material);
  shaft.position.set(0, 0.05, -sizeMeters / 2);

  const headGeom = new THREE.ConeGeometry(0.4, 1, 12);
  headGeom.rotateX(-Math.PI / 2);
  const head = new THREE.Mesh(headGeom, material);
  head.position.set(0, 0.05, -sizeMeters);

  group.add(shaft, head);
  return group;
}

/** Dispose a helper object's geometry and material(s). */
export function disposeHelper(object: THREE.Object3D): void {
  object.traverse((obj) => {
    const mesh = obj as Partial<THREE.Mesh & THREE.LineSegments>;
    mesh.geometry?.dispose();
    const material = mesh.material;
    if (Array.isArray(material)) {
      material.forEach((m) => m.dispose());
    } else {
      material?.dispose();
    }
  });
}
